/**
 * The desktop bridge.
 *
 * Everything the renderer can ask the Electron main process for lives behind
 * window.pogu. In a browser tab it is simply absent, and every caller copes.
 */

export type UpdateState = {
  status: 'idle' | 'checking' | 'available' | 'downloading' | 'ready' | 'error' | 'current';
  current: string | null;
  latest: string | null;
  notes: string[];
  progress: number;
  error: string | null;
};

export type ScreenSource = {
  id: string;
  name: string;
  kind: 'screen' | 'window';
  thumbnail: string | null;
  icon: string | null;
};

export type PugoBridge = {
  window: (action: 'minimize' | 'maximize' | 'fullscreen' | 'close') => Promise<boolean | void>;
  notify: (
    title: string,
    body: string,
    opts?: { icon?: string; route?: string; urgent?: boolean; kindLabel?: string }
  ) => Promise<boolean>;
  openExternal: (url: string) => Promise<void>;
  copy: (text: string) => Promise<boolean>;
  info: () => Promise<{ version: string; platform: string }>;
  setCloseToTray: (enabled: boolean) => Promise<boolean>;
  setBadge: (count: number) => Promise<number>;

  /**
   * Starting a game.
   *
   * Desktop only, and undefined in a browser - a web page cannot be allowed
   * to hand the operating system a command, which is exactly what a
   * steam:// address is. The address comes from the server.
   */
  game?: {
    launch: (url: string) => Promise<{ ok: boolean; reason?: string; scheme?: string }>;
    schemes: () => Promise<string[]>;
    /** Whether this game is on the machine, and where Pugo thinks it is. */
    find: (gameId: string) => Promise<{
      gameId: string;
      chosenPath: string | null;
      found: boolean;
      how: 'chosen' | 'steam' | 'installed' | null;
      file: string | null;
      searchable: boolean;
    }>;
    /** Runs the installed copy. */
    run: (gameId: string) => Promise<{ ok: boolean; reason?: string; how?: string; file?: string }>;
    /**
     * Closes a game that is running.
     *
     * The id only: the shell keeps the table of executables and refuses any
     * id it does not know, so nothing here can name a process to kill.
     */
    stop: (gameId: string) => Promise<{ ok: boolean; reason?: string; closed?: number }>;
    /** Opens a file picker so somebody can point Pugo at their own copy. */
    pick: (gameId: string) => Promise<{ ok: boolean; reason?: string; file?: string | null }>;
    forget: (gameId: string) => Promise<{ ok: boolean; file?: string | null }>;
  };

  /** The detached voice dock. Desktop only; undefined in a browser. */
  dock?: {
    /** Whether it floats above other programs, and clings to screen edges. */
    behaviour?: () => Promise<{ onTop: boolean; snap: boolean; snapPx: number }>;
    setOnTop?: (on: boolean) => Promise<boolean>;
    setSnap?: (on: boolean) => Promise<boolean>;
    open: () => Promise<boolean>;
    close: () => Promise<boolean>;
    isOpen: () => Promise<boolean>;
    /** Tells the main process this window is listening, so it can replay. */
    ready: () => Promise<boolean>;
    /** Shrinks the window down to a pill, and back. */
    collapse: (on: boolean) => Promise<boolean>;
    onCollapsed: (fn: (on: boolean) => void) => () => void;
    push: (state: unknown) => Promise<boolean>;
    act: (action: string) => Promise<boolean>;
    onState: (fn: (state: never) => void) => () => void;
    onAction: (fn: (action: string) => void) => () => void;
  };
  show: () => Promise<boolean>;
  quit: () => Promise<void>;
  onWindowState: (fn: (s: { maximized: boolean; fullscreen?: boolean }) => void) => () => void;
  /** Fires when a notification is clicked, with the hash route it points at. */
  onRoute: (fn: (route: string) => void) => () => void;
  setStartup: (enabled: boolean) => Promise<boolean>;
  /** What Windows will actually do, Task Manager included. */
  getStartup: () => Promise<{ registered: boolean; enabled: boolean }>;
  /** A copy of the sign-in token, outside the renderer's own storage. */
  saveSession: (token: string | null) => Promise<boolean>;
  loadSession: () => Promise<string | null>;
  screenSources: () => Promise<ScreenSource[]>;
  pickScreen: (id: string) => Promise<boolean>;
  update: {
    check: (serverUrl: string) => Promise<UpdateState>;
    download: () => Promise<UpdateState>;
    install: () => Promise<boolean>;
    state: () => Promise<UpdateState>;
    onState: (fn: (s: UpdateState) => void) => () => void;
    onOpen: (fn: () => void) => () => void;
  };
  onShortcut: (fn: (action: string) => void) => () => void;
  /**
   * Fires when what you are playing changes.
   *
   * The whole list, because people run more than one thing. Older builds of
   * the shell sent a single id or null; the handler copes with both.
   */
  onGame: (fn: (games: string[] | string | null) => void) => () => void;

  /** Seconds since the machine was last touched, for going away by itself. */
  idleSeconds?: () => Promise<number>;
  /** Registers your bound combos with the OS. Resolves with what it could not claim. */
  setGlobalHotkeys?: (binds: Record<string, string>) => Promise<{
    /** Actions the machine would not give us at all. */
    failed: string[];
    /** True when keys are watched and passed on, false when claimed outright. */
    passthrough?: boolean;
  }>;
  /** App size as real page zoom; the window's minimum size grows to match. */
  setZoom?: (factor: number) => Promise<boolean>;
  versions: { electron: string; chrome: string; node: string };
  platform: string;
};

declare global {
  interface Window { pogu?: PugoBridge; callout?: Partial<PugoBridge> }
}

export function bridge(): PugoBridge | null {
  if (typeof window === 'undefined') return null;
  return (window.pogu ?? null) as PugoBridge | null;
}

export function isDesktop(): boolean {
  return !!bridge();
}

/** Open a link in the real browser when we can, otherwise a new tab. */
export function openExternal(url: string): void {
  const api = bridge();
  if (api?.openExternal) void api.openExternal(url);
  else window.open(url, '_blank', 'noopener');
}

/**
 * Copy, three ways. navigator.clipboard only exists in a secure context and
 * the web build is served over plain http, so it is genuinely absent there.
 */
export async function copyText(text: string): Promise<boolean> {
  const value = String(text ?? '');

  const api = bridge();
  if (api?.copy) {
    try { await api.copy(value); return true; } catch { /* fall through */ }
  }
  if (navigator.clipboard && window.isSecureContext) {
    try { await navigator.clipboard.writeText(value); return true; } catch { /* fall through */ }
  }
  try {
    const holder = document.createElement('textarea');
    holder.value = value;
    holder.setAttribute('readonly', '');
    holder.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0';
    document.body.append(holder);
    holder.select();
    const ok = document.execCommand('copy');
    holder.remove();
    return ok;
  } catch {
    return false;
  }
}
